<template>
    <div class="login-container">
        <div class="login-shell">
            <loginHeader class="login-page-header"></loginHeader>

            <div class="login-main">
                <section class="login-hero">
                    <h1 class="hero-title">
                        {{$t('login.heroPrefix')}}<span>{{$t('login.heroInvoicing')}}</span>{{$t('login.heroMiddle')}}<br>
                        <strong>{{$t('login.heroSimplified')}}</strong>{{$t('login.heroSuffix')}}
                    </h1>
                    <p class="hero-subtitle">
                        {{$t('login.subtitle1')}}<br>
                        {{$t('login.subtitle2')}}
                    </p>

                    <div class="feature-list">
                        <div class="feature-item">
                            <div class="feature-icon feature-icon-blue">
                                <i class="el-icon-document"></i>
                            </div>
                            <div>
                                <div class="feature-title">{{$t('login.feature1Title')}}</div>
                                <div class="feature-text">{{$t('login.feature1Text')}}</div>
                            </div>
                        </div>
                        <div class="feature-item">
                            <div class="feature-icon feature-icon-purple">
                                <i class="el-icon-data-line"></i>
                            </div>
                            <div>
                                <div class="feature-title">{{$t('login.feature2Title')}}</div>
                                <div class="feature-text">{{$t('login.feature2Text')}}</div>
                            </div>
                        </div>
                        <div class="feature-item">
                            <div class="feature-icon feature-icon-green">
                                <i class="el-icon-lock"></i>
                            </div>
                            <div>
                                <div class="feature-title">{{$t('login.feature3Title')}}</div>
                                <div class="feature-text">{{$t('login.feature3Text')}}</div>
                            </div>
                        </div>
                    </div>

                    <img class="login-illustration" src="@/static/swift_invoice_login_illustration_no_logo.png" alt="">
                </section>

                <section class="login-card-wrap">
                    <el-form class="card-box login-form" autoComplete="on" :model="loginForm" :rules="loginRules"
                             ref="loginForm" key="login-form"
                             v-if="authMode === 'login'"
                             label-position="left">
                        <div class="form-heading">
                            <h2>{{$t('login.welcomeBack')}}</h2>
                            <p>{{$t('login.signInToContinue')}}</p>
                        </div>

                        <el-form-item prop="email" class="formusername">
                            <label class="field-label">{{$t('common.email')}}</label>
                            <el-input name="email" autofocus="true" prefix-icon="el-icon-message" type="email"
                                      v-model="loginForm.email" autoComplete="username" :placeholder="$t('login.enterEmail')"
                                      @keyup.native.enter="handleLogin"/>
                        </el-form-item>

                        <el-form-item prop="password" class="formpassword">
                            <label class="field-label">{{$t('common.password')}}</label>
                            <el-input name="password" prefix-icon="el-icon-lock" type="password"
                                      v-model="loginForm.password" autoComplete="current-password"
                                      :placeholder="$t('login.enterPassword')" @keyup.native.enter="handleLogin"/>
                        </el-form-item>

                        <div class="login-options-row">
                            <el-checkbox v-model="rememberMe">{{$t('login.rememberMe')}}</el-checkbox>
                            <button type="button" class="forgot-password-button" @click="openForgotPasswordDialog">
                                {{$t('login.forgotPassword')}}
                            </button>
                        </div>

                        <el-button type="primary" class="sign-in-button" :disabled="loginDisabled" :loading="loading"
                                   @click.native.prevent="handleLogin">{{$t('login.signIn')}}
                        </el-button>

                        <div class="social-auth-divider">
                            <span>{{$t('common.or')}}</span>
                        </div>

                        <button type="button" class="google-sign-in-button" :disabled="googleLoading || socialAuthDisabled"
                                :loading="googleLoading" @click.prevent="handleGoogleLogin">
                            <span class="google-logo" aria-hidden="true">
                                <svg width="18" height="18" viewBox="0 0 48 48">
                                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                                </svg>
                            </span>
                            <span>{{ googleLoading ? $t('login.signingIn') : $t('login.continueWithGoogle') }}</span>
                        </button>

                        <button type="button" class="apple-sign-in-button" :disabled="appleLoading || socialAuthDisabled"
                                @click.prevent="handleAppleLogin">
                            <img src="@/static/icon/apple.svg" alt="" class="apple-logo">
                            <span>{{ appleLoading ? $t('login.signingIn') : $t('login.continueWithApple') }}</span>
                        </button>

                        <button type="button" class="switch-auth-button" @click="switchToRegister">
                            {{$t('login.createAccount')}}
                        </button>

                    </el-form>

                    <el-form class="card-box login-form" autoComplete="on" :model="registerForm"
                             :rules="registerRules" ref="registerForm" key="register-form" v-else label-position="left">
                        <div class="form-heading">
                            <h2>{{$t('login.createAccount')}}</h2>
                            <p>{{$t('login.startUsing')}}</p>
                        </div>

                        <el-form-item prop="companyName">
                            <label class="field-label">{{$t('login.companyName')}}</label>
                            <el-input name="companyName" prefix-icon="el-icon-office-building" type="text"
                                      v-model="registerForm.companyName" autoComplete="organization"
                                      :placeholder="$t('login.enterCompanyName')"/>
                        </el-form-item>

                        <el-form-item prop="email">
                            <label class="field-label">{{$t('common.email')}}</label>
                            <el-input name="registerEmail" prefix-icon="el-icon-message" type="email"
                                      v-model="registerForm.email" autoComplete="username" :placeholder="$t('login.enterEmail')"/>
                        </el-form-item>

                        <el-form-item prop="password">
                            <label class="field-label">{{$t('common.password')}}</label>
                            <el-input name="registerPassword" prefix-icon="el-icon-lock" type="password"
                                      v-model="registerForm.password" autoComplete="new-password"
                                      :placeholder="$t('login.enterPassword')" @keyup.native.enter="handleRegister"/>
                        </el-form-item>

                        <div class="turnstile-section">
                            <div ref="turnstileContainer" class="turnstile-widget"></div>
                            <p v-if="turnstileError" class="turnstile-error">{{turnstileError}}</p>
                        </div>

                        <el-button type="primary" class="sign-in-button" :disabled="registerDisabled"
                                   :loading="registerLoading" @click.native.prevent="handleRegister">{{$t('login.createAccount')}}
                        </el-button>

                        <div class="social-auth-divider">
                            <span>{{$t('common.or')}}</span>
                        </div>

                        <button type="button" class="google-sign-in-button" :disabled="googleLoading || socialAuthDisabled"
                                :loading="googleLoading" @click.prevent="handleGoogleLogin">
                            <span class="google-logo" aria-hidden="true">
                                <svg width="18" height="18" viewBox="0 0 48 48">
                                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                                </svg>
                            </span>
                            <span>{{ googleLoading ? $t('login.signingIn') : $t('login.continueWithGoogle') }}</span>
                        </button>

                        <button type="button" class="apple-sign-in-button" :disabled="appleLoading || socialAuthDisabled"
                                @click.prevent="handleAppleLogin">
                            <img src="@/static/icon/apple.svg" alt="" class="apple-logo">
                            <span>{{ appleLoading ? $t('login.signingIn') : $t('login.continueWithApple') }}</span>
                        </button>

                        <button type="button" class="switch-auth-button" @click="switchToLogin">
                            {{$t('login.alreadyHaveAccount')}}
                        </button>

                    </el-form>
                </section>
            </div>

            <div class="login-footer">
                <span>{{$t('login.footerRights')}}</span>
                <a href="https://swiftinvoice.io/termsofservice/" target="_blank" rel="noopener noreferrer">{{$t('login.termsOfService')}}</a>
                <span>{{$t('login.contactUs')}}</span>
            </div>
        </div>

        <el-dialog :visible.sync="openBeta" width="40%">
            <div style="font-size: 20px;line-height: 2;" v-html="$t('login.betaDialogBody')"></div>
        </el-dialog>

        <el-dialog :visible.sync="openIE" width="40%">
            <div style="font-size: 20px;line-height: 2;">
                {{$t('login.ieDialogBody')}}
                <a href="https://www.google.com/chrome/">chrome</a>
            </div>
        </el-dialog>

        <el-dialog :title="$t('login.resetPasswordTitle')" :visible.sync="forgotPasswordDialog" width="420px"
                   custom-class="reset-password-dialog">
            <el-form :model="forgotPasswordForm" :rules="forgotPasswordRules" ref="forgotPasswordForm"
                     label-position="top">
                <el-form-item prop="email" :label="$t('common.email')">
                    <el-input prefix-icon="el-icon-message" type="email" autocomplete="username"
                              v-model="forgotPasswordForm.email"
                              :placeholder="$t('login.enterEmail')"></el-input>
                </el-form-item>
            </el-form>
            <span slot="footer" class="dialog-footer">
                <el-button @click="forgotPasswordDialog = false">{{$t('common.cancel')}}</el-button>
                <el-button type="primary" :disabled="forgotPasswordDisabled" :loading="forgotPasswordLoading" @click="sendResetPasswordEmail">
                    {{$t('login.sendResetEmail')}}
                </el-button>
            </span>
        </el-dialog>

        <el-dialog :title="$t('login.bindingTitle')" :visible.sync="bindingDialog" width="420px"
                   :close-on-click-modal="false" :close-on-press-escape="!bindingLoading"
                   :show-close="!bindingLoading" custom-class="binding-dialog">
            <div class="binding-dialog-body">
                <p class="binding-hint">
                    {{$t('login.bindingHintPrefix')}}
                    <strong>{{bindingMaskedEmail}}</strong>{{$t('login.bindingHintSuffix')}}
                </p>
                <el-form :model="bindingForm" :rules="bindingRules" ref="bindingForm" label-position="top"
                         @submit.native.prevent>
                    <el-form-item prop="password" :label="$t('common.password')">
                        <el-input type="password" autocomplete="current-password" v-model="bindingForm.password"
                                  prefix-icon="el-icon-lock" :placeholder="$t('login.enterAccountPassword')"
                                  @keyup.native.enter="handleBindConfirm"></el-input>
                    </el-form-item>
                </el-form>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button :disabled="bindingLoading" @click="closeBindingDialog">{{$t('common.cancel')}}</el-button>
                <el-button type="primary" :disabled="bindingDisabled" :loading="bindingLoading" @click="handleBindConfirm">{{$t('login.linkAccount')}}</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import {validateEmail} from '@/utils/validate'
    import loginHeader from '../../components/common/loginHeader.vue'
    import {initializeCompanySelection} from '@/utils/company'
    import {markCurrentTabAuthenticated} from '@/utils/authTabState'
    import {
        PROVIDER_GOOGLE,
        PROVIDER_APPLE,
        signInWithGoogle,
        initGoogleSignIn,
        signInWithApple,
        socialLogin,
        bindSocialLogin
    } from '@/utils/socialLogin'

    export default {
        components: {
            loginHeader
        },
        data: function () {
            const validUsername = (rule, value, callback) => {
                if (!validateEmail(value)) {
                    callback(new Error(this.$t('login.validEmail')))
                } else {
                    callback()
                }
            }
            const validPassword = (rule, value, callback) => {
                if (value.length < 6) {
                    callback(new Error(this.$t('login.validPassword')))
                } else {
                    callback()
                }
            }
            return {
                openBeta: false,
                authMode: 'login',
                loginForm: {
                    email: '',
                    password: ''
                },
                registerForm: {
                    companyName: '',
                    email: '',
                    password: ''
                },
                forgotPasswordForm: {
                    email: ''
                },
                loginRules: {
                    email: [{required: true, trigger: 'blur', validator: validUsername}],
                    password: [{required: true, trigger: 'blur', validator: validPassword}]
                },
                registerRules: {
                    companyName: [],
                    email: [{required: true, trigger: 'blur', validator: validUsername}],
                    password: [{required: true, trigger: 'blur', validator: validPassword}]
                },
                forgotPasswordRules: {
                    email: [{required: true, trigger: 'blur', validator: validUsername}]
                },
                loading: false,
                registerLoading: false,
                forgotPasswordLoading: false,
                forgotPasswordDialog: false,
                rememberMe: false,
                openIE: false,
                cantLogin:false,
                googleLoading: false,
                appleLoading: false,
                bindingDialog: false,
                bindingLoading: false,
                bindingMaskedEmail: '',
                bindingTicket: '',
                bindingForm: {
                    password: ''
                },
                turnstileToken: '',
                turnstileWidgetId: null,
                turnstileLoading: false,
                turnstileError: '',
                bindingRules: {
                    password: [{required: true, trigger: 'blur', validator: validPassword}]
                }
            }
        },
        watch: {
            $route() {
                this.applyAuthModeFromRoute();
                this.$nextTick(() => {
                    const form = this.authMode === 'register' ? this.$refs.registerForm : this.$refs.loginForm;
                    if (form) {
                        form.clearValidate();
                    }
                    this.syncTurnstileWidget();
                });
            },
            '$i18n.locale'() {
                this.refreshVisibleValidationMessages();
            }
        },
        computed: {
            loginDisabled() {
                return this.cantLogin || this.loading || this.loginForm.email === '' || this.loginForm.password === '';
            },
            registerDisabled() {
                return this.cantLogin || this.registerLoading
                    || this.registerForm.email === '' || this.registerForm.password === ''
                    || this.turnstileToken === '';
            },
            socialAuthDisabled() {
                return this.cantLogin;
            },
            forgotPasswordDisabled() {
                return this.forgotPasswordLoading || this.forgotPasswordForm.email === '';
            },
            bindingDisabled() {
                return this.bindingLoading || this.bindingForm.password === '';
            }
        },
        methods: {
            refreshVisibleValidationMessages() {
                this.$nextTick(() => {
                    ['loginForm', 'registerForm', 'forgotPasswordForm', 'bindingForm'].forEach((refName) => {
                        const form = this.$refs[refName];
                        if (!form || !Array.isArray(form.fields)) {
                            return;
                        }
                        const invalidProps = form.fields
                            .filter(field => field.validateState === 'error' && field.prop)
                            .map(field => field.prop);
                        if (invalidProps.length === 0) {
                            return;
                        }
                        form.clearValidate();
                        this.$nextTick(() => {
                            if (this.$refs[refName] !== form) {
                                return;
                            }
                            invalidProps.forEach(prop => form.validateField(prop));
                        });
                    });
                });
            },
            clickBeta() {
                this.openBeta = true;
            },
            waitForTurnstile(maxWaitMs) {
                return new Promise((resolve, reject) => {
                    if (window.turnstile) {
                        resolve(window.turnstile);
                        return;
                    }
                    const start = Date.now();
                    const timer = setInterval(() => {
                        if (window.turnstile) {
                            clearInterval(timer);
                            resolve(window.turnstile);
                        } else if (Date.now() - start > maxWaitMs) {
                            clearInterval(timer);
                            reject(new Error('Turnstile script not loaded'));
                        }
                    }, 50);
                });
            },
            renderTurnstileWidget() {
                if (this.authMode !== 'register' || this.turnstileWidgetId != null || this.turnstileLoading) {
                    return;
                }
                const siteKey = process.env.VUE_APP_TURNSTILE_SITE_KEY;
                if (!siteKey) {
                    this.turnstileError = this.$t('login.turnstileUnavailable');
                    return;
                }
                this.turnstileLoading = true;
                this.waitForTurnstile(10000).then((turnstile) => {
                    this.turnstileLoading = false;
                    if (this.authMode !== 'register' || !this.$refs.turnstileContainer) {
                        return;
                    }
                    this.turnstileWidgetId = turnstile.render(this.$refs.turnstileContainer, {
                        sitekey: siteKey,
                        action: 'register',
                        size: 'flexible',
                        callback: (token) => {
                            this.turnstileToken = token;
                            this.turnstileError = '';
                        },
                        'expired-callback': () => {
                            this.turnstileToken = '';
                            this.turnstileError = this.$t('login.turnstileExpired');
                        },
                        'error-callback': () => {
                            this.turnstileToken = '';
                            this.turnstileError = this.$t('login.turnstileUnavailable');
                        }
                    });
                }).catch(() => {
                    this.turnstileLoading = false;
                    this.turnstileError = this.$t('login.turnstileUnavailable');
                });
            },
            resetTurnstileWidget() {
                this.turnstileToken = '';
                if (this.turnstileWidgetId != null && window.turnstile) {
                    window.turnstile.reset(this.turnstileWidgetId);
                }
            },
            removeTurnstileWidget() {
                if (this.turnstileWidgetId != null && window.turnstile) {
                    window.turnstile.remove(this.turnstileWidgetId);
                }
                this.turnstileWidgetId = null;
                this.turnstileToken = '';
                this.turnstileError = '';
            },
            syncTurnstileWidget() {
                if (this.authMode === 'register') {
                    this.renderTurnstileWidget();
                } else {
                    this.removeTurnstileWidget();
                }
            },
            applyAuthModeFromRoute() {
                if (this.$route.meta.authMode === 'register') {
                    this.authMode = 'register';
                    return;
                }
                this.authMode = 'login';
            },
            switchToRegister() {
                this.$router.push('/createAccount');
            },
            switchToLogin() {
                this.$router.push('/login');
            },
            openForgotPasswordDialog() {
                this.forgotPasswordForm.email = this.loginForm.email;
                this.forgotPasswordDialog = true;
                this.$nextTick(() => {
                    this.$refs.forgotPasswordForm.clearValidate();
                });
            },
            loginAfterAuthResponse(responseData, loadingKey) {
                let self = this;
                if (this.rememberMe) {
                    localStorage.setItem('rememberLoginEmail', this.loginForm.email);
                } else {
                    localStorage.removeItem('rememberLoginEmail');
                }
                localStorage.setItem('loginInformation', JSON.stringify(responseData));
                markCurrentTabAuthenticated(responseData);
                this.$axios.post('query_setting_and_company', {
                    params: {}
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        initializeCompanySelection(response.data);
                        self.$router.replace('/invoice');
                    }
                    self[loadingKey] = false;
                }).catch(function () {
                    self[loadingKey] = false;
                })
            },
            handleLogin() {
                if (this.loginDisabled) {
                    return;
                }
                let self = this;
                this.$refs.loginForm.validate((valid) => {
                    if (valid) {
                        this.loading = true;
                        this.$axios.post('login.htm', {
                            params: {
                                email: this.loginForm.email,
                                password: this.loginForm.password
                            }
                        }).then(response => {
                            if (response.data.errorCode === 0) {
                                this.loginAfterAuthResponse(response.data, 'loading');
                            } else {
                                self.loading = false;
                            }
                        }).catch(function () {
                            self.loading = false;
                        })
                    } else {
                        return false;
                    }
                });
            },
            handleRegister() {
                if (this.registerDisabled) {
                    return;
                }
                let self = this;
                this.$refs.registerForm.validate((valid) => {
                    if (valid) {
                        this.registerLoading = true;
                        this.$axios.post('create_invoice_user', {
                            params: {
                                companyName: this.registerForm.companyName.trim(),
                                email: this.registerForm.email,
                                password: this.registerForm.password,
                                fromClient: 3,
                                createTime: Math.floor(Date.now() / 1000),
                                turnstileToken: this.turnstileToken
                            }
                        }).then(response => {
                            if (response.data.errorCode === 0) {
                                this.loginAfterAuthResponse(response.data, 'registerLoading');
                            } else {
                                self.registerLoading = false;
                                self.resetTurnstileWidget();
                            }
                        }).catch(function () {
                            self.registerLoading = false;
                            self.resetTurnstileWidget();
                        })
                    } else {
                        return false;
                    }
                });
            },
            sendResetPasswordEmail() {
                if (this.forgotPasswordDisabled) {
                    return;
                }
                let self = this;
                this.$refs.forgotPasswordForm.validate((valid) => {
                    if (valid) {
                        this.forgotPasswordLoading = true;
                        this.$axios.post('send_reset_password_email', {
                            params: {
                                email: this.forgotPasswordForm.email
                            }
                        }).then(response => {
                            self.forgotPasswordLoading = false;
                            if (response.data.errorCode === 0) {
                                self.forgotPasswordDialog = false;
                                self.$message({
                                    type: 'success',
                                    message: self.$t('login.resetEmailSent')
                                });
                            }
                        }).catch(function () {
                            self.forgotPasswordLoading = false;
                        })
                    } else {
                        return false;
                    }
                });
            },
            handleGoogleLogin() {
                this.handleSocialLogin(PROVIDER_GOOGLE, signInWithGoogle, 'googleLoading');
            },
            handleAppleLogin() {
                this.handleSocialLogin(PROVIDER_APPLE, signInWithApple, 'appleLoading');
            },
            handleSocialLogin(provider, signInFn, loadingKey) {
                let self = this;
                if (this[loadingKey] || this.socialAuthDisabled) {
                    return;
                }
                this[loadingKey] = true;
                signInFn().then(function (result) {
                    const companyName = self.authMode === 'register' ? self.registerForm.companyName.trim() : '';
                    return socialLogin(self.$axios, {
                        provider: provider,
                        idToken: result.idToken,
                        rawNonce: result.rawNonce,
                        companyName: companyName
                    });
                }).then(function (response) {
                    const data = response.data;
                    if (data.errorCode === 0) {
                        self[loadingKey] = false;
                        self.loginAfterAuthResponse(data, loadingKey);
                        return;
                    }
                    if (data.errorCode === 228 && data.objectData != null) {
                        // BINDING_REQUIRED: email already used by an existing account.
                        self.bindingTicket = data.objectData.bindingTicket || '';
                        self.bindingMaskedEmail = data.objectData.maskedEmail || '';
                        self.bindingForm.password = '';
                        self.bindingDialog = true;
                        self[loadingKey] = false;
                        return;
                    }
                    // Other error codes are surfaced by the axios response interceptor.
                    self[loadingKey] = false;
                    if (self.authMode === 'register') {
                        self.resetTurnstileWidget();
                    }
                }).catch(function () {
                    self[loadingKey] = false;
                    if (self.authMode === 'register') {
                        self.resetTurnstileWidget();
                    }
                });
            },
            handleBindConfirm() {
                let self = this;
                if (this.bindingDisabled) {
                    return;
                }
                this.$refs.bindingForm.validate((valid) => {
                    if (!valid) {
                        return false;
                    }
                    self.bindingLoading = true;
                    bindSocialLogin(self.$axios, {
                        bindingTicket: self.bindingTicket,
                        password: self.bindingForm.password
                    }).then(function (response) {
                        const data = response.data;
                        if (data.errorCode === 0) {
                            self.bindingLoading = false;
                            self.bindingDialog = false;
                            self.loginAfterAuthResponse(data, 'bindingLoading');
                            return;
                        }
                        self.bindingLoading = false;
                    }).catch(function () {
                        self.bindingLoading = false;
                    });
                });
            },
            closeBindingDialog() {
                if (this.bindingLoading) {
                    return;
                }
                this.bindingDialog = false;
                this.bindingTicket = '';
                this.bindingMaskedEmail = '';
                this.bindingForm.password = '';
                if (this.$refs.bindingForm) {
                    this.$refs.bindingForm.clearValidate();
                }
            }
        },
        mounted() {
            this.applyAuthModeFromRoute();

            // Pre-render the hidden Google Sign-In button so the user's first
            // click opens the account-picker popup synchronously.
            initGoogleSignIn();

            let userAgent = navigator.userAgent;
            let isOpera = userAgent.indexOf("Opera") > -1;
            if (userAgent.indexOf("compatible") > -1 && userAgent.indexOf("MSIE") > -1 && !isOpera) {
                //IE浏览器,不让用
                this.openIE = true;
            }

            if (this.openIE) {
                this.cantLogin = true;
            }

            const rememberedEmail = localStorage.getItem('rememberLoginEmail');
            if (rememberedEmail != null && rememberedEmail !== '') {
                this.loginForm.email = rememberedEmail;
                this.rememberMe = true;
            }
            this.$nextTick(() => this.syncTurnstileWidget());
        },
        beforeDestroy() {
            this.removeTurnstileWidget();
        }
    }
</script>

<style scoped>
    .login-container {
        min-height: 100vh;
        text-align: left;
        background:
                radial-gradient(circle at 16% 20%, rgba(13, 125, 247, 0.12), transparent 32%),
                radial-gradient(circle at 70% 10%, rgba(113, 72, 255, 0.09), transparent 26%),
                linear-gradient(135deg, #f7fbff 0%, #ffffff 50%, #f4f8ff 100%);
    }

    .login-shell {
        width: min(1320px, calc(100vw - 96px));
        min-height: 100vh;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        padding: 36px 0 56px;
        box-sizing: border-box;
    }

    .login-page-header {
        flex: 0 0 auto;
    }

    .login-main {
        flex: 1;
        display: grid;
        grid-template-columns: minmax(460px, 1fr) minmax(430px, 520px);
        align-items: center;
        gap: 90px;
        padding-top: 0;
    }

    .login-hero {
        position: relative;
        min-height: 680px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        overflow: hidden;
        padding-bottom: 250px;
        box-sizing: border-box;
    }

    .hero-title {
        margin: 0;
        color: #14234a;
        font-size: 48px;
        line-height: 1.18;
        font-weight: 700;
    }

    .hero-title span {
        color: #0d6dfd;
    }

    .hero-title strong {
        color: #7a3ffc;
        font-weight: 700;
    }

    .hero-subtitle {
        margin: 28px 0 0;
        color: #566584;
        font-size: 18px;
        line-height: 1.55;
    }

    .feature-list {
        display: flex;
        flex-direction: column;
        gap: 24px;
        margin-top: 44px;
    }

    .feature-item {
        display: flex;
        align-items: center;
        gap: 16px;
    }

    .feature-icon {
        width: 42px;
        height: 42px;
        flex: 0 0 42px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        color: #ffffff;
        font-size: 20px;
        box-shadow: 0 14px 26px rgba(37, 99, 235, 0.18);
    }

    .feature-icon-blue {
        background: linear-gradient(135deg, #2f8cff, #1168ef);
    }

    .feature-icon-purple {
        background: linear-gradient(135deg, #9567ff, #6927f2);
    }

    .feature-icon-green {
        background: linear-gradient(135deg, #31dcc2, #10bfa2);
    }

    .feature-title {
        color: #132040;
        font-size: 16px;
        font-weight: 600;
    }

    .feature-text {
        margin-top: 4px;
        color: #61708d;
        font-size: var(--responsive-font-14);
    }

    .login-illustration {
        position: absolute;
        left: -24px;
        bottom: -110px;
        width: min(560px, 94%);
        max-width: none;
        pointer-events: none;
        user-select: none;
    }

    .login-card-wrap {
        display: flex;
        justify-content: center;
        padding-top: 0;
    }

    .login-form {
        width: 100%;
        max-width: 500px;
        padding: 54px 48px 48px;
        box-sizing: border-box;
        border: 1px solid rgba(198, 208, 224, 0.82);
        border-radius: var(--ui-radius-panel);
        background: rgba(255, 255, 255, 0.86);
        box-shadow: 0 24px 70px rgba(31, 45, 74, 0.14);
        backdrop-filter: blur(10px);
    }

    .form-heading {
        text-align: center;
        margin-bottom: 36px;
    }

    .form-heading h2 {
        margin: 0;
        color: #14234a;
        font-size: 28px;
        line-height: 1.25;
        font-weight: 700;
    }

    .form-heading p {
        margin: 12px 0 0;
        color: #61708d;
        font-size: 15px;
    }

    .field-label {
        display: block;
        margin-bottom: 8px;
        color: #17213c;
        font-size: 15px;
        font-weight: 500;
    }

    .login-form /deep/ .el-form-item {
        margin-bottom: 20px;
    }

    .login-form /deep/ .el-input__inner {
        height: 48px;
        border: 1px solid #d7dee9;
        border-radius: var(--ui-radius-control);
        color: #17213c;
        font-size: 15px;
        background: rgba(255, 255, 255, 0.92);
    }

    .login-form /deep/ .el-input__inner:focus {
        border-color: #0d7df7;
        box-shadow: none;
    }

    .login-form /deep/ .el-input__prefix {
        color: #8190aa;
    }

    .sign-in-button {
        width: 100%;
        height: 48px;
        margin-top: 8px;
        border: 0;
        border-radius: var(--ui-radius-control);
        background: linear-gradient(135deg, #0d6dfd, #0d7df7);
        box-shadow: 0 14px 28px rgba(13, 125, 247, 0.24);
        font-size: 16px;
        font-weight: 600;
    }

    .sign-in-button:hover,
    .sign-in-button:focus {
        background: linear-gradient(135deg, #0c63e7, #0d74ea);
    }

    .turnstile-section {
        min-height: 65px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        margin: 2px 0 10px;
    }

    .turnstile-widget {
        width: 100%;
        display: flex;
        justify-content: center;
    }

    .turnstile-error {
        width: 100%;
        margin: 8px 0 0;
        color: #f56c6c;
        font-size: 13px;
        line-height: 18px;
        text-align: center;
    }

    .login-options-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: -4px 0 24px;
    }

    .login-options-row /deep/ .el-checkbox {
        color: #17213c;
        font-size: var(--responsive-font-14);
        font-weight: 500;
    }

    .login-options-row /deep/ .el-checkbox__label {
        color: #17213c;
        font-size: var(--responsive-font-14);
        font-weight: 500;
    }

    .forgot-password-button {
        display: inline-flex;
        margin: 0;
        padding: 0;
        border: 0;
        color: #0d6dfd;
        background: transparent;
        font-size: var(--responsive-font-14);
        font-weight: 500;
        line-height: 20px;
        cursor: pointer;
    }

    .forgot-password-button:hover,
    .switch-auth-button:hover {
        color: #0b5ed7;
    }

    .switch-auth-button {
        width: 100%;
        margin-top: 24px;
        padding: 0;
        border: 0;
        color: #0d6dfd;
        background: transparent;
        font-size: 15px;
        font-weight: 600;
        line-height: 22px;
        cursor: pointer;
    }

    .social-auth-divider {
        display: flex;
        align-items: center;
        text-align: center;
        margin: 24px 0 16px;
        color: #8190aa;
        font-size: 13px;
    }

    .social-auth-divider::before,
    .social-auth-divider::after {
        content: "";
        flex: 1;
        height: 1px;
        background: #e2e8f1;
    }

    .social-auth-divider span {
        padding: 0 14px;
    }

    .google-sign-in-button {
        width: 100%;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        border: 1px solid #d7dee9;
        border-radius: var(--ui-radius-control);
        background: #ffffff;
        color: #17213c;
        font-size: 15px;
        font-weight: 500;
        cursor: pointer;
        transition: background 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
    }

    .google-sign-in-button:hover:not(:disabled) {
        background: #f6f9ff;
        border-color: #b9c6db;
        box-shadow: 0 6px 16px rgba(31, 45, 74, 0.08);
    }

    .google-sign-in-button:disabled {
        opacity: 0.65;
        cursor: not-allowed;
    }

    .google-logo {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        flex: 0 0 18px;
    }

    .apple-sign-in-button {
        width: 100%;
        height: 48px;
        margin-top: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        border: 1px solid #000000;
        border-radius: var(--ui-radius-control);
        background: #000000;
        color: #ffffff;
        font-size: 15px;
        font-weight: 500;
        cursor: pointer;
        transition: background 180ms ease, box-shadow 180ms ease, opacity 180ms ease;
    }

    .apple-sign-in-button:hover:not(:disabled) {
        background: #1a1a1a;
        box-shadow: 0 10px 22px rgba(0, 0, 0, 0.18);
    }

    .apple-sign-in-button:disabled {
        opacity: 0.65;
        cursor: not-allowed;
    }

    .apple-logo {
        width: 18px;
        height: 18px;
        flex: 0 0 18px;
        /* The apple.svg has no fill (defaults to black); invert to white on the dark button. */
        filter: brightness(0) invert(1);
    }

    .binding-dialog .binding-dialog-body {
        padding-top: 4px;
    }

    .binding-dialog .binding-hint {
        margin: 0 0 18px;
        color: #566584;
        font-size: var(--responsive-font-14);
        line-height: 1.6;
    }

    .binding-dialog .binding-hint strong {
        color: #17213c;
    }

    .login-footer {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 18px;
        margin-top: 24px;
        color: #61708d;
        font-size: 13px;
        line-height: 20px;
        text-align: center;
    }

    .login-footer a {
        color: #61708d;
        text-decoration: none;
    }

    .login-footer a:hover {
        color: #0d6dfd;
    }

    .reset-password-dialog /deep/ .el-dialog__body {
        padding-bottom: 8px;
    }

    @media (max-width: 1440px) {
        .login-shell {
            width: min(1180px, calc(100vw - 48px));
            padding: 22px 0 24px;
        }

        .login-main {
            grid-template-columns: minmax(360px, 1fr) minmax(400px, 460px);
            gap: 48px;
        }

        .login-hero {
            min-height: 600px;
            padding-bottom: 190px;
        }

        .hero-title {
            font-size: 40px;
        }

        .hero-subtitle {
            margin-top: 20px;
            font-size: 16px;
        }

        .feature-list {
            gap: 16px;
            margin-top: 30px;
        }

        .feature-icon {
            width: 38px;
            height: 38px;
            flex-basis: 38px;
            font-size: 18px;
        }

        .login-illustration {
            bottom: -95px;
            width: min(480px, 92%);
        }

        .login-form {
            max-width: 460px;
            padding: 32px 36px 30px;
        }

        .form-heading {
            margin-bottom: 24px;
        }

        .form-heading h2 {
            font-size: 25px;
        }

        .form-heading p {
            margin-top: 8px;
            font-size: var(--responsive-font-14);
        }

        .field-label {
            margin-bottom: 8px;
            font-size: var(--responsive-font-14);
        }

        .login-form /deep/ .el-form-item {
            margin-bottom: 16px;
        }

        .login-form /deep/ .el-input__inner {
            height: 48px;
            font-size: var(--responsive-font-14);
        }

        .login-options-row {
            margin: -2px 0 16px;
        }

        .sign-in-button,
        .google-sign-in-button,
        .apple-sign-in-button {
            height: 48px;
            font-size: var(--responsive-font-14);
        }

        .sign-in-button {
            margin-top: 4px;
        }

        .social-auth-divider {
            margin: 16px 0 14px;
        }

        .apple-sign-in-button {
            margin-top: 12px;
        }

        .switch-auth-button {
            margin-top: 16px;
            font-size: var(--responsive-font-14);
        }

        .login-footer {
            margin-top: 16px;
        }
    }

    @media (max-width: 1100px) {
        .login-container {
            min-height: auto;
        }

        .login-shell {
            width: min(560px, calc(100vw - 48px));
            min-height: auto;
            padding: 20px 0 28px;
        }

        .login-main {
            grid-template-columns: 1fr;
            gap: 0;
            align-items: start;
            padding-top: 24px;
        }

        .login-hero {
            display: none;
        }

        .login-illustration {
            display: none;
        }
    }

    @media (max-width: 640px) {
        .login-shell {
            width: calc(100vw - 32px);
            min-height: auto;
            padding: 20px 0 40px;
        }

        .login-main {
            padding-top: 24px;
            gap: 28px;
        }

        .login-hero {
            padding-bottom: 0;
            min-height: auto;
        }

        .hero-title {
            font-size: 30px;
            line-height: 1.2;
        }

        .hero-subtitle {
            margin-top: 18px;
            font-size: 15px;
            line-height: 1.5;
        }

        .feature-list {
            gap: 14px;
            margin-top: 28px;
        }

        .feature-item {
            gap: 12px;
        }

        .feature-icon {
            width: 36px;
            height: 36px;
            flex: 0 0 36px;
            font-size: 17px;
        }

        .login-form {
            padding: 32px 24px 28px;
            border-radius: var(--ui-radius-panel);
            box-shadow: 0 18px 44px rgba(31, 45, 74, 0.1);
        }

        .form-heading {
            margin-bottom: 24px;
        }

        .form-heading h2 {
            font-size: 22px;
        }

        .login-form /deep/ .el-input__inner {
            height: 48px;
            font-size: var(--responsive-font-14);
        }

        .sign-in-button,
        .google-sign-in-button,
        .apple-sign-in-button {
            height: 48px;
            font-size: var(--responsive-font-14);
        }

        .apple-sign-in-button {
            margin-top: 12px;
        }

        .login-options-row {
            align-items: flex-start;
            flex-direction: column;
            gap: 12px;
        }

        .switch-auth-button {
            margin-top: 16px;
            font-size: var(--responsive-font-14);
        }
    }
</style>
